import { useFormikContext } from "formik";
import { useCallback, useMemo, useState } from "react";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import type { RowSelectionState, Updater } from "@tanstack/react-table";
import type {
    ApplyCloseTemplateEntityRow,
    ApplyCloseTemplateWizardValues,
} from "./applyCloseTemplate.schema";
import { useApplyCloseTemplateEntityBulkActions } from "./useApplyCloseTemplateEntityBulkActions";
import { useTemplateApplication } from "./useTemplateApplication";
import {
    applyRowSelectionToEntityRows,
    buildRowSelectionFromEntityRows,
    resolveRowSelectionUpdater,
} from "./applyCloseTemplateEntitySelectionRowSelection";

interface Params {
    filteredEntityIds: Set<string>;
    activeRoles: Set<TaskTemplateAssigneeRole>;
}

export function useApplyCloseTemplateEntityBulkSelection({
    filteredEntityIds,
    activeRoles,
}: Params) {
    const formik = useFormikContext<ApplyCloseTemplateWizardValues>();
    const { setFormikState } = formik;

    // ── Bulk-action selection (left checkboxes) ────────────────────────────
    const [bulkRowSelection, setBulkRowSelection] = useState<RowSelectionState>(
        {},
    );

    const onRowSelectionChange = useCallback(
        (updater: Updater<RowSelectionState>) => {
            setBulkRowSelection((prev) =>
                resolveRowSelectionUpdater(updater, prev),
            );
        },
        [],
    );

    // ── Entity-application selection (right SELECT column) ────────────────
    const onToggleRows = useCallback(
        (rowIds: string[], selected: boolean) => {
            setFormikState((state) => {
                const prev = buildRowSelectionFromEntityRows(
                    state.values.entityRows,
                );
                const next = { ...prev };
                for (const id of rowIds) {
                    if (selected) {
                        next[id] = true;
                    } else {
                        delete next[id];
                    }
                }
                return {
                    ...state,
                    values: {
                        ...state.values,
                        entityRows: applyRowSelectionToEntityRows(
                            state.values.entityRows,
                            state.values.defaults,
                            next,
                        ),
                    },
                };
            });
        },
        [setFormikState],
    );

    const tableMeta = useMemo(
        () => ({ callbacks: { onToggleRows } }),
        [onToggleRows],
    );

    // ── Bulk member update ──────────────────────────────────────────────────
    const { members, getMemberLabel } = useTemplateApplication();

    const handleBulkMemberUpdate = useCallback(
        (
            ids: string[],
            field: keyof Pick<
                ApplyCloseTemplateEntityRow,
                "preparerId" | "reviewerId" | "managerId"
            >,
            value: number | null,
        ) => {
            setFormikState((state) => {
                const entityRows = { ...state.values.entityRows };
                for (const id of ids) {
                    const row = entityRows[id];
                    if (row) {
                        entityRows[id] = { ...row, [field]: value };
                    }
                }
                return {
                    ...state,
                    values: { ...state.values, entityRows },
                };
            });
        },
        [setFormikState],
    );

    const selectedBulkIds = useMemo(
        () =>
            Object.entries(bulkRowSelection)
                .filter(
                    ([id, isSelected]) =>
                        isSelected && filteredEntityIds.has(id),
                )
                .map(([id]) => id),
        [bulkRowSelection, filteredEntityIds],
    );

    const selectedEntityWorkspaceIds = useMemo(
        () =>
            selectedBulkIds.flatMap((id) => {
                const wsId = formik.values.entityRows[id]?.workspaceId;
                return wsId ? [wsId] : [];
            }),
        [selectedBulkIds, formik.values.entityRows],
    );

    const handleClearBulkSelection = useCallback(() => {
        setBulkRowSelection({});
    }, []);

    const {
        handleOpenBulkActions,
        handleOpenBulkPreparer,
        handleOpenBulkReviewer,
        handleOpenBulkManager,
    } = useApplyCloseTemplateEntityBulkActions({
        selectedBulkIds,
        selectedEntityWorkspaceIds,
        activeRoles,
        members,
        getMemberLabel,
        onBulkUpdate: handleBulkMemberUpdate,
        clearBulkSelection: handleClearBulkSelection,
    });

    const totalFilteredEntityCount = useMemo(
        () => filteredEntityIds.size,
        [filteredEntityIds],
    );

    const handleSelectAll = useCallback(() => {
        setBulkRowSelection((prev) => {
            const next = { ...prev };
            for (const id of filteredEntityIds) {
                next[id] = true;
            }
            return next;
        });
    }, [filteredEntityIds]);

    // ── Application-selection bulk controls (via left-checkbox selection) ────
    // These act on the isSelected flag in entityRows (which entities the template
    // will be applied to) for the currently left-checkbox-selected rows.

    const handleSelectForApplication = useCallback(() => {
        onToggleRows(selectedBulkIds, true);
        handleClearBulkSelection();
    }, [selectedBulkIds, onToggleRows, handleClearBulkSelection]);

    const handleDeselectForApplication = useCallback(() => {
        onToggleRows(selectedBulkIds, false);
        handleClearBulkSelection();
    }, [selectedBulkIds, onToggleRows, handleClearBulkSelection]);

    const handleUnselectSelected = useCallback(() => {
        setBulkRowSelection((prev) => {
            const next = { ...prev };
            for (const id of filteredEntityIds) {
                delete next[id];
            }
            return next;
        });
    }, [filteredEntityIds]);

    const hasAllSelectedBulkEntitiesSelected = useMemo(
        () =>
            selectedBulkIds.length > 0 &&
            selectedBulkIds.every(
                (id) => formik.values.entityRows[id]?.isSelected ?? false,
            ),
        [formik.values.entityRows, selectedBulkIds],
    );

    const hasSelectedEntities = useMemo(
        () =>
            Object.values(formik.values.entityRows).some(
                (row) => row.isSelected,
            ),
        [formik.values.entityRows],
    );

    return {
        bulkRowSelection,
        onRowSelectionChange,
        tableMeta,
        selectedBulkIds,
        totalFilteredEntityCount,
        hasSelectedEntities,
        hasAllSelectedBulkEntitiesSelected,
        handleSelectAll,
        handleUnselectSelected,
        handleOpenBulkActions,
        handleOpenBulkPreparer,
        handleOpenBulkReviewer,
        handleOpenBulkManager,
        handleSelectForApplication,
        handleDeselectForApplication,
    };
}
